import { isEvaluatorVersionAlignable } from './useCanAlignEvaluators';
import { useCurrentOrganization } from './useSwr';

export enum OrgConfigs {
  can_add_seats = 'can_add_seats',
  can_use_rbac = 'can_use_rbac',
  max_identities = 'max_identities',
  max_run_rules = 'max_run_rules',
  premier_plan_approval_date = 'premier_plan_approval_date',
  startup_plan_approval_date = 'startup_plan_approval_date',
  connected_to_metronome = 'connected_to_metronome',
  connected_to_stripe = 'connected_to_stripe',
  is_personal = 'is_personal',
  payment_method = 'payment_method',
  max_workspaces = 'max_workspaces',
  can_serve_datasets = 'can_serve_datasets',
  can_use_langgraph_cloud = 'can_use_langgraph_cloud',
  max_langgraph_cloud_deployments = 'max_langgraph_cloud_deployments',
  max_free_langgraph_cloud_deployments = 'max_free_langgraph_cloud_deployments',
  can_use_agent_builder = 'can_use_agent_builder',
  use_python_playground_service = 'use_python_playground_service',
  datadog_rum_session_sample_rate = 'datadog_rum_session_sample_rate',
  max_prompt_webhooks = 'max_prompt_webhooks',

  // Feature flags
  show_playground_prompt_canvas = 'show_playground_prompt_canvas',
  kv_dataset_message_support = 'kv_dataset_message_support',
  allow_custom_iframes = 'allow_custom_iframes',
  enable_langgraph_pricing = 'enable_langgraph_pricing',
  enable_thread_view_playground = 'enable_thread_view_playground',
  enable_org_usage_charts = 'enable_org_usage_charts',

  use_exact_search_for_prompts = 'use_exact_search_for_prompts',
  langgraph_deploy_own_cloud_enabled = 'langgraph_deploy_own_cloud_enabled',
  prompt_optimization_jobs_enabled = 'prompt_optimization_jobs_enabled',
  demo_lgp_new_graph_enabled = 'demo_lgp_new_graph_enabled',
  langgraph_remote_reconciler_enabled = 'langgraph_remote_reconciler_enabled',
  langsmith_alerts_poc_enabled = 'langsmith_alerts_poc_enabled',
  lgp_templates_enabled = 'lgp_templates_enabled',
  langsmith_alerts_legacy_poc_enabled = 'langsmith_alerts_legacy_poc_enabled',
  langsmith_experimental_search_enabled = 'langsmith_experimental_search_enabled',
  enable_align_evaluators = 'enable_align_evaluators',
  enable_run_tree_streaming = 'enable_run_tree_streaming',
  enable_threads_improvements = 'enable_threads_improvements',
  enable_monthly_usage_charts = 'enable_monthly_usage_charts',
  new_rule_evaluator_creation_version = 'new_rule_evaluator_creation_version',
  enable_lgp_listeners_page = 'enable_lgp_listeners_page',
  org_scoped_api_keys_enabled = 'org_scoped_api_keys_enabled',
  clio_enabled = 'clio_enabled',
  enable_include_extended_stats = 'enable_include_extended_stats',
  enable_markdown_in_studio = 'enable_markdown_in_studio',
  require_code_evaluator_language_field = 'require_code_evaluator_language_field',
  feedback_formulas_enabled = 'feedback_formulas_enabled',
  enable_markdown_in_tracing = 'enable_markdown_in_tracing',
  enable_pricing_redesign = 'enable_pricing_redesign',
  thread_evaluators_enabled = 'thread_evaluators_enabled',
}

const DEFAULT_ORG_CONFIGS = {
  can_add_seats: false,
  can_use_rbac: false,
  max_identities: 5,
  max_run_rules: 25,
  premier_plan_approval_date: '',
  startup_plan_approval_date: '',
  connected_to_metronome: false,
  connected_to_stripe: false,
  is_personal: false,
  payment_method: '',
  can_serve_datasets: false,
  can_use_langgraph_cloud: false,
  max_langgraph_cloud_deployments: 3,
  max_free_langgraph_cloud_deployments: 0,
  can_use_agent_builder: false,
  use_python_playground_service: false,

  show_playground_prompt_canvas: false,
  kv_dataset_message_support: false,
  allow_custom_iframes: false,
  enable_langgraph_pricing: false,
  enable_thread_view_playground: false,
  enable_org_usage_charts: false,
  use_exact_search_for_prompts: false,
  langgraph_deploy_own_cloud_enabled: false,
  demo_lgp_new_graph_enabled: false,
  langgraph_remote_reconciler_enabled: false,
  langsmith_alerts_poc_enabled: true,
  lgp_templates_enabled: false,
  langsmith_alerts_legacy_poc_enabled: false,
  langsmith_experimental_search_enabled: false,
  enable_align_evaluators: false,
  enable_run_tree_streaming: false,
  enable_threads_improvements: false,
  max_prompt_webhooks: 1,
  enable_monthly_usage_charts: false,
  new_rule_evaluator_creation_version: 2,
  enable_lgp_listeners_page: false,
  clio_enabled: false,
  enable_include_extended_stats: false,
  enable_markdown_in_studio: true,
  require_code_evaluator_language_field: false,
  feedback_formulas_enabled: false,
  enable_markdown_in_tracing: false,
  enable_pricing_redesign: false,
  thread_evaluators_enabled: false,
};

export const useOrgConfig = (
  config: OrgConfigs
): {
  isLoading: boolean;
  value: string | number | boolean | null;
} => {
  const { data: organizationConfig, isLoading } = useCurrentOrganization();

  // There is an old version and a new version of the alignEvals flow. enable_align_evaluators is the old feature flag.
  // The new flow is simply gated by new_rule_evaluator_creation_version - evaluators are alignable if this number is >= 3
  // If this new flow is enabled, we should disable the old flow since the new flow is more robust.
  if (config === OrgConfigs.enable_align_evaluators) {
    const alignEvaluators = isLoading
      ? null
      : organizationConfig?.config[config] ?? DEFAULT_ORG_CONFIGS[config];
    const alignEvaluatorsV2 = isLoading
      ? null
      : isEvaluatorVersionAlignable(
          organizationConfig?.config[
            OrgConfigs.new_rule_evaluator_creation_version
          ] ??
            DEFAULT_ORG_CONFIGS[OrgConfigs.new_rule_evaluator_creation_version]
        );
    return {
      isLoading,
      value: alignEvaluators && !alignEvaluatorsV2,
    };
  }

  return {
    isLoading,
    value: isLoading
      ? null
      : organizationConfig?.config[config] ?? DEFAULT_ORG_CONFIGS[config],
  };
};

export const useOrgDisabled = (): {
  isDisabled: boolean;
  isLoading: boolean;
} => {
  const { data: organizationConfig, isLoading } = useCurrentOrganization();
  return {
    isLoading,
    isDisabled: !!organizationConfig?.disabled,
  };
};
